/*
 * Single source of truth for the "an active delivery channel requires at least one payment
 * method" invariant. Extracted from 3 independent implementations that had already drifted once:
 * `ConfigureDeliverySection.tsx` used to *say* it reused `validateDeliveryCommerceValues` from the
 * panel, but never did — a previous session discovered that and wrote a second implementation
 * from scratch instead of fixing the reuse. See
 * docs/plan-unificacion-validaciones-formularios.md for the full context.
 *
 * The 4th copy lived in the orphan `src/pages/OnBoarding/steps/DeliveryMethods.tsx` (unreachable
 * since the wizard was cut to 2 steps in f6fbaef0). It was deleted in this same PR: the onboarding
 * step came back as `steps/DeliveryMethodsV4.tsx`, which consumes `DELIVERY_CHANNELS` from here.
 * That step adds one restriction of its own — the enabled method must be UNIVERSAL (cash/card/
 * transfer), a payment provider alone is not enough to finish signup. That narrowing is
 * deliberately NOT hoisted here: outside onboarding a commerce may charge however it wants.
 */
import type { Commerce } from '@interfaces/general';

export const DELIVERY_CHANNELS = [
  { channel: 'activar_envio_domicilio', methods: 'metodos_pago_envio_domicilio', label: 'Envío a domicilio' },
  { channel: 'activar_pasar_recoger', methods: 'metodos_pago_pasar_recoger', label: 'Pasar a recoger' },
  { channel: 'activar_comer_restaurant', methods: 'metodos_pago_comer_restaurant', label: 'Comer en el restaurante' },
] as const;

export type ChannelKey = (typeof DELIVERY_CHANNELS)[number]['channel'];
type MethodsKey = (typeof DELIVERY_CHANNELS)[number]['methods'];

const CHANNEL_TO_METHODS: Record<ChannelKey, MethodsKey> = DELIVERY_CHANNELS.reduce(
  (acc, { channel, methods }) => ({ ...acc, [channel]: methods }),
  {} as Record<ChannelKey, MethodsKey>,
);

// Generic scan over every key of the methods object — this intentionally includes
// `payment_provider` and `enlace_pago`, not just the 3 "universal" methods (efectivo/tarjeta/
// transferencia). Losing `payment_provider` here would flip which commerces are considered
// "accepts a payment method" in a production data cleanup currently in flight (440 vs 462
// commerces) — commerces that deliberately only charge through a payment provider would suddenly
// look like they need cash/card/transfer enabled.
const hasAnyPaymentMethod = (methods: Commerce[MethodsKey] | undefined): boolean =>
  Boolean(methods) && Object.values(methods as object).some((enabled) => enabled === true);

/**
 * True if the channel is active and has at least one payment method enabled.
 * @param {Commerce} commerce - Full commerce object
 * @param {ChannelKey} channel - Delivery channel to check (one of DELIVERY_CHANNELS' `channel` keys)
 * @returns {boolean} True if the channel is active and has at least one payment method enabled
 */
export const channelHasPaymentMethod = (commerce: Commerce, channel: ChannelKey): boolean =>
  Boolean(commerce[channel]) && hasAnyPaymentMethod(commerce[CHANNEL_TO_METHODS[channel]]);

/**
 * Active channels missing every payment method. Empty array means the invariant holds.
 * @param {Commerce} commerce - Full commerce object
 * @returns {ChannelKey[]} The active channels that have no payment method enabled
 */
export const activeChannelsWithoutPayment = (commerce: Commerce): ChannelKey[] =>
  DELIVERY_CHANNELS.filter(
    ({ channel }) => Boolean(commerce[channel]) && !channelHasPaymentMethod(commerce, channel),
  ).map(({ channel }) => channel);

/**
 * True if any ACTIVE channel accepts bank transfer (drives the bank-details requirement).
 * @param {Commerce} commerce - Full commerce object
 * @returns {boolean} True if any active channel has `transferencia` enabled
 */
export const anyActiveChannelAcceptsTransfer = (commerce: Commerce): boolean =>
  DELIVERY_CHANNELS.some(
    ({ channel, methods }) => Boolean(commerce[channel]) && Boolean(commerce[methods]?.transferencia),
  );
